import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { AVAILABILITY_OPTIONS } from '@webapp/core/constants/inquiry-labels';
import { InquiryServiceType } from '@webapp/core/enums';
import { CreateInquiryStep4 } from '@webapp/core/interfaces';
import { addDays, format } from 'date-fns';
import { ReCaptchaV3Service } from 'ng-recaptcha';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import {
  InquiryDraftSource,
  PublicInquiryManagerService,
} from 'projects/webapp/app/services/public-inquiry-manager.service';
import { futureDate, nextDate } from 'projects/webapp/app/utils/validators';
import { Subject, switchMap, takeUntil } from 'rxjs';


/** Termin & Kontakt, the last step of every flow. Sends the inquiry. */
@Component({
  selector: 'app-inquiry-step4',
  templateUrl: './step4.component.html',
})
export class InquiryStep4Component implements OnInit, OnDestroy {
  draftInquiryForm = new FormGroup({
    first_name: new FormControl('', { validators: Validators.required, nonNullable: true }),
    last_name: new FormControl('', { validators: Validators.required, nonNullable: true }),
    phone: new FormControl(
      '',
      { validators: [Validators.required, Validators.pattern(/^[\+]?[0-9\s\-\(\)]{7,20}$/)], nonNullable: true }
    ),
    email: new FormControl('', { validators: [Validators.required, Validators.email], nonNullable: true }),

    date: new FormControl('', { validators: [Validators.required, futureDate()], nonNullable: true }),
    is_date_flexible: new FormControl(false, { nonNullable: true }),
    handover_date: new FormControl<string | null>(''),

    availability: new FormControl<Set<string>>(new Set(), { nonNullable: true }),
    newsletter: new FormControl(false, { nonNullable: true }),
    accept_terms: new FormControl(false, { validators: Validators.requiredTrue, nonNullable: true }),
  }, { validators: nextDate('date', 'handover_date') });

  availabilityOptions = AVAILABILITY_OPTIONS;

  /** `futureDate()` turns today down, so the date pickers start tomorrow. */
  minDate = format(addDays(new Date(), 1), 'yyyy-MM-dd');

  isSubmitting = false;

  get isCleaningOnly() {
    return this._publicInquiryManagerService.inquiryCreationProgress?.service === InquiryServiceType.CLEAN;
  }

  protected _destroy$ = new Subject<void>();

  protected _draftSource: InquiryDraftSource = () => ({ step4: this._buildPayload() });

  constructor(
    protected _toastrService: ToastrService,
    protected _spinnerService: NgxSpinnerService,
    protected _publicInquiryManagerService: PublicInquiryManagerService,
    protected _recaptcha: ReCaptchaV3Service,
  ) {
  }

  ngOnInit() {
    this._loadFormData();
    this._publicInquiryManagerService.setDraftSource(this._draftSource);
  }

  ngOnDestroy() {
    this._publicInquiryManagerService.clearDraftSource(this._draftSource);
    this._destroy$.next();
    this._destroy$.complete();
  }

  hasError(controlName: string, errorName?: string) {
    const control = this.draftInquiryForm.get(controlName);
    const isValid = errorName ? !control?.hasError(errorName) : !control?.invalid;
    return !!control?.touched && !isValid;
  }

  isAvailabilitySelected(value: string) {
    return this.draftInquiryForm.controls.availability.value.has(value);
  }

  toggleAvailability(value: string) {
    const values = this.draftInquiryForm.controls.availability.value;
    if (values.has(value)) values.delete(value);
    else values.add(value);
  }

  onSubmit() {
    if (this.isSubmitting) return;

    this.draftInquiryForm.markAllAsTouched();
    if (!this.draftInquiryForm.valid) {
      this._toastrService.warning('Bitte füllen Sie alle Pflichtfelder aus.');
      return;
    }

    this.isSubmitting = true;
    this._spinnerService.show();
    const data = this._buildPayload();

    this._recaptcha.execute('public_inquiry')
      .pipe(
        takeUntil(this._destroy$),
        switchMap(captchaToken => this._publicInquiryManagerService.submitStep4(data, captchaToken)),
      )
      .subscribe({
        next: () => {
          this._toastrService.success('Ihre Anfrage wurde erfolgreich übermittelt!')
        },
        error: (error) => {
          console.error('Error submitting inquiry:', error);
          this._toastrService.error('Ups, etwas ist schief gelaufen. Bitte versuchen Sie es später nochmals.');
        }
      })
      .add(() => {
        this.isSubmitting = false;
        this._spinnerService.hide();
      });
  }

  goBack() {
    this._publicInquiryManagerService.goPrevStep()
  }

  protected _loadFormData() {
    const step4Data = this._publicInquiryManagerService.inquiryCreationProgress?.step4;
    if (!step4Data) return;

    this.draftInquiryForm.patchValue({ ...step4Data, availability: new Set(step4Data.availability ?? []) });
  }

  protected _buildPayload(): CreateInquiryStep4 {
    const v = this.draftInquiryForm.getRawValue();
    return { ...v, handover_date: v.handover_date || null, availability: [...v.availability] };
  }
}
